import React, { useCallback, useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Label, Image, ProgressIndicator } from '@fluentui/react';
import { TileHeader } from '@bingads-webui-campaign-react/goals-and-website-tile';

import { getStyles, getStylesForFluentUi } from './styles';
import animationImg from '../media/animation.gif';

const TEXT_SWITCHING_INTERVAL = 3 * 1000;

export function ProcessingAnimation(props) {
  const {
    title,
    description,
    rollingTexts,
    rollingInterval,
    mobileDeviceQuery,
    customImg,
    showIcon,
    useProgressIndicator,
    displayMode,
  } = props;
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);

  const [rollingTextIndex, setRollingTextIndex] = useState(0);
  const timerRef = useRef(null);

  const styles = getStylesForFluentUi(mobileDeviceQuery);

  const stopRolling = useCallback(() => {
    if (timerRef && timerRef.current) {
      clearInterval(timerRef.current);
    }
    setRollingTextIndex(0);
  }, []);

  const startRolling = useCallback(() => {
    if (timerRef && timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      if (rollingTextIndex === rollingTexts.length - 1) {
        setRollingTextIndex(0);
      } else {
        setRollingTextIndex(rollingTextIndex + 1);
      }
    }, rollingInterval);
  }, [rollingInterval, rollingTextIndex, rollingTexts.length]);

  useEffect(() => {
    startRolling();
  }, [startRolling]);

  useEffect(() => () => {
    stopRolling();
  }, [stopRolling]);

  return (
    <div className="processing-animation-container">
      <TileHeader
        title={title}
        subtitle={<span className="animation-description">{description}</span>}
        displayMode={displayMode}
        hideIcon={!showIcon}
      />
      <div className={classNames('animation-container', classes.animationContainer)}>
        {useProgressIndicator && <ProgressIndicator />}
        {!useProgressIndicator && <Image
          src={customImg || animationImg}
          alt={i18n.getString(_TL_('website animation'))}
          styles={styles.animationImage}
          className="animation-img"
        />}
        <Label styles={styles.animationLabel}>
          {rollingTexts[rollingTextIndex]}
        </Label>
      </div>
    </div>
  );
}

ProcessingAnimation.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  rollingTexts: PropTypes.arrayOf(PropTypes.string),
  rollingInterval: PropTypes.number,
  mobileDeviceQuery: PropTypes.string,
  customImg: PropTypes.string,
  displayMode: PropTypes.string,
  showIcon: PropTypes.bool,
  useProgressIndicator: PropTypes.bool,
};

ProcessingAnimation.defaultProps = {
  title: '',
  description: '',
  rollingTexts: [],
  rollingInterval: TEXT_SWITCHING_INTERVAL,
  mobileDeviceQuery: '@media (max-width:600px)',
  customImg: null,
  displayMode: '',
  showIcon: false,
  useProgressIndicator: false,
};
